import { useEffect, useRef, useState } from "react";
import {
  ColorType,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";

import { useWsSubscribe } from "@/ws/WebSocketProvider";
import type { WatchSymbol } from "./WatchlistPane";

/**
 * Lightweight-charts candle pane. Synthesizes 1-second candles from the
 * `tick` WebSocket events while we don't have a real OHLCV feed. When
 * `/piggy/api/price-data/{symbol}` is wired to a live source the pane
 * can swap to seeding from there.
 */
export function ChartPane({ symbol }: { symbol: WatchSymbol }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const maSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const candlesRef = useRef<Map<number, { open: number; high: number; low: number; close: number }>>(new Map());
  const [timeframe, setTimeframe] = useState<"1s" | "5s" | "30s">("1s");

  // Build chart once per mount.
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: "rgb(12, 16, 28)" },
        textColor: "rgb(148, 163, 184)",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      timeScale: { borderColor: "rgb(30, 41, 59)", timeVisible: true, secondsVisible: true },
      rightPriceScale: { borderColor: "rgb(30, 41, 59)" },
      crosshair: { mode: 0 },
      autoSize: true,
    });
    chartRef.current = chart;
    const candle = chart.addCandlestickSeries({
      upColor: "rgb(16, 185, 129)",
      downColor: "rgb(239, 68, 68)",
      borderVisible: false,
      wickUpColor: "rgb(16, 185, 129)",
      wickDownColor: "rgb(239, 68, 68)",
    });
    candleSeriesRef.current = candle;
    const ma = chart.addLineSeries({
      color: "rgb(255, 200, 0)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    maSeriesRef.current = ma;

    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      maSeriesRef.current = null;
    };
  }, []);

  // Reset on symbol change.
  useEffect(() => {
    candlesRef.current.clear();
    candleSeriesRef.current?.setData([]);
    maSeriesRef.current?.setData([]);
  }, [symbol.symbol]);

  // Aggregate live ticks into OHLCV buckets.
  const bucketSeconds = timeframe === "1s" ? 1 : timeframe === "5s" ? 5 : 30;
  function applyTick(msg: Record<string, unknown>) {
    if (msg.symbol !== symbol.symbol) return;
    const bid = Number(msg.bid ?? 0);
    const ask = Number(msg.ask ?? 0);
    const px = bid && ask ? (bid + ask) / 2 : Number(msg.price ?? 0);
    if (!px) return;

    const t = Math.floor((Number(msg.timestamp ?? msg.ts ?? Date.now() / 1000) ?? 0) / bucketSeconds) * bucketSeconds;
    const map = candlesRef.current;
    const cur = map.get(t);
    if (!cur) {
      map.set(t, { open: px, high: px, low: px, close: px });
    } else {
      cur.high = Math.max(cur.high, px);
      cur.low = Math.min(cur.low, px);
      cur.close = px;
    }

    const candles = Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([time, c]) => ({ time: time as UTCTimestamp, ...c }));

    if (candles.length > 600) candles.splice(0, candles.length - 600);
    candleSeriesRef.current?.setData(candles);

    // Simple 20-candle moving average overlay.
    const period = 20;
    const ma: { time: UTCTimestamp; value: number }[] = [];
    for (let i = period - 1; i < candles.length; i++) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
      ma.push({ time: candles[i].time, value: sum / period });
    }
    maSeriesRef.current?.setData(ma);
  }
  // Subscribe to both broker-pivot client_tick and legacy tick events.
  useWsSubscribe("client_tick", applyTick);
  useWsSubscribe("tick", applyTick);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between border-b border-term-border bg-term-bg px-2 py-1">
        <div className="flex items-baseline gap-2">
          <span className="tnum text-sm font-semibold text-term-fg">{symbol.symbol}</span>
          <span className="text-2xs uppercase tracking-wider text-term-mute">{symbol.asset_class}</span>
        </div>
        <div className="flex gap-1">
          {(["1s", "5s", "30s"] as const).map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`rounded-sm px-1.5 py-0.5 text-2xs font-mono ${
                timeframe === tf
                  ? "bg-brand text-black"
                  : "text-term-dim hover:bg-term-elev"
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>
      <div ref={containerRef} className="flex-1" />
    </div>
  );
}
